import type { ButtonProps as ChakraButtonProps } from '@chakra-ui/react'
import {
  AbsoluteCenter,
  Button as ChakraButton,
  Span,
  Spinner,
} from '@chakra-ui/react'
import * as React from 'react'

import type { ViewportConfettiOptions } from '@/utils/confetti/viewportConfetti'
import { fireViewportConfettiFromElement } from '@/utils/confetti/viewportConfetti'

export type { ViewportConfettiOptions } from '@/utils/confetti/viewportConfetti'

interface ButtonLoadingProps {
  loading?: boolean
  loadingText?: React.ReactNode
}

export interface ButtonProps extends ChakraButtonProps, ButtonLoadingProps {
  /**
   * On click, fire confetti from the button (canvas viewport).
   * Options = [canvas-confetti](https://github.com/catdad/canvas-confetti#confettioptions-object--promisenull)
   * (also see [react-canvas-confetti](https://ulitcos.github.io/react-canvas-confetti/) which relies on it).
   */
  confetti?: false | ViewportConfettiOptions
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {
      loading,
      disabled,
      loadingText,
      children,
      confetti: confettiOptions,
      onClick,
      ...rest
    } = props

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e)
        if (confettiOptions === false || confettiOptions == null) return
        const el = e.currentTarget
        queueMicrotask(() => {
          fireViewportConfettiFromElement(el, confettiOptions)
        })
      },
      [onClick, confettiOptions]
    )

    const clickProps =
      confettiOptions !== false && confettiOptions != null
        ? { onClick: handleClick }
        : { onClick }

    return (
      <ChakraButton
        disabled={loading || disabled}
        ref={ref}
        {...rest}
        {...clickProps}
      >
        {}
        {loading && !loadingText ? (
          <>
            <AbsoluteCenter display="inline-flex">
              <Spinner size="inherit" color="inherit" />
            </AbsoluteCenter>
            <Span opacity={0}>{children}</Span>
          </>
        ) : loading && loadingText ? (
          <>
            <Spinner size="inherit" color="inherit" />
            {loadingText}
          </>
        ) : (
          children
        )}
      </ChakraButton>
    )
  }
)
